import React, { useState } from "react";
import { SubmitHandler, get, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FormElementModel } from "../../models/FormElementModel";
import { FinalizeRecipient } from "../../models/FinalizeRecipient";
import { State } from "../../store";
import { InputsFinalize } from "../FinalizePage/FinalizeNoLogin";
import { invoiceForm, userPayForm } from "../FinalizePage/inputsForm/InputPayFormModels";
import InvoiceButtons from "../FinalizePage/InvoiceButtons";
import RecipientAndPurchaserForm from "../FinalizePage/RecipientAndPurchaserForm";
import ConsentInputs from "../FinalizePage/ConsentInputs/ConsentInputs";
import EATextField from "../shared/TextField/EATextField";
import EANipInput from "../shared/NipInput/EANipInput";
import EAPhoneInput from "../shared/PhoneInput/EAPhoneInput";
import EAButton from "../shared/Buttons/EAButton";
import EAAlert from "../shared/EAAlert";
import LoadingSpinner from "../../../features/Loading/LoadingSpinner";
import { useCountryCodes } from "../FinalizePage/hooks/useCountryCodes";
import useSwimmingCourseTransaction from "./useSwimmingCourseTransaction";

interface IProps {
  onCancel: () => void;
  priceLabel: string;
}

type InvoiceType = "noInvoice" | "customerInvoice" | "companyInvoice" | "recipientAndPurchaserInvoice";

const SwimmingCourseOfferForm: React.FC<IProps> = ({ onCancel, priceLabel }) => {
  const { t } = useTranslation();
  const saleConfiguration = useSelector((state: State) => state.config.configuration);
  const zipCodeRequired = useSelector((state: State) => state.config.configuration.zipCodeRequired);
  const phoneNumberRequired = useSelector((state: State) => state.config.configuration.phoneNumberRequired);
  const { countryCodes, hasError: countryCodesError } = useCountryCodes();
  const { saveTransaction, loading, error, paymentUrl } = useSwimmingCourseTransaction();

  const [invoiceType, setInvoiceType] = useState<InvoiceType>("noInvoice");
  const [email, setEmail] = useState<string>();
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const {
    register,
    watch,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<InputsFinalize>({});

  const showInvoiceOptions = saleConfiguration.invoiceActive;

  // assignPass/transaction (inaczej niż saveReservation) wymaga wypełnionego `purchaser`
  // zawsze, gdy createInvoice=true — nie tylko dla typu "odbiorca+nabywca". Dla pozostałych
  // typów faktury budujemy go sami z pól, które user już wpisał w formularzu.
  const buildSelfPurchaser = (data: InputsFinalize): FinalizeRecipient => ({
    name:
      invoiceType === "companyInvoice" ? data.companyName : `${data.firstName} ${data.lastName}`.trim(),
    vatId: invoiceType === "companyInvoice" ? data.NIP : "",
    locality: data.city,
    postCode: data.zipCode,
    street: data.street,
    houseNumber: data.houseNumber,
    flatNumber: data.flatNumber,
    countryCode: invoiceType === "companyInvoice" ? data.nipCountryCode : undefined,
  });

  const handleRegistration: SubmitHandler<InputsFinalize> = async (data) => {
    if (hasSubmitted) return;
    setEmail(data.email);
    setHasSubmitted(true);

    const createInvoice = invoiceType !== "noInvoice";

    const cleanedData: InputsFinalize = {
      ...data,
      NIP: invoiceType === "companyInvoice" ? data.NIP : "",
      companyName: invoiceType === "companyInvoice" ? data.companyName : "",
      nipCountryCode: invoiceType === "companyInvoice" ? data.nipCountryCode : undefined,
      // react-hook-form nie czyści wartości pól po odmontowaniu (brak shouldUnregister),
      // więc gdyby user wcześniej wypełnił "odbiorca+nabywca" i przełączył typ faktury,
      // stare dane by tu zostały mimo że formularz już ich nie pokazuje.
      recipient: invoiceType === "recipientAndPurchaserInvoice" ? data.recipient : undefined,
      purchaser:
        invoiceType === "recipientAndPurchaserInvoice"
          ? data.purchaser
          : createInvoice
            ? buildSelfPurchaser(data)
            : undefined,
      createInvoice,
    };

    const success = await saveTransaction(cleanedData);
    if (!success) {
      setHasSubmitted(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleRegistration)} noValidate>
      <div className="swimming-course-offer__form-section">
        <h4 className="swimming-course-offer__form-title">{t("dane_zamawiajacego")}</h4>

        <div className="swimming-course-offer__form-fields">
          {userPayForm(watch, register, zipCodeRequired ?? false).map(
            (item: FormElementModel, index: number) => {
              if (!item.name) return null;
              if (item.name === "zipCode" && showInvoiceOptions && invoiceType !== "noInvoice") return null;
              return (
                <EATextField
                  key={index}
                  label={item.text}
                  name={item.name}
                  type={item.type as "text" | "email" | "password" | "number"}
                  placeholder={item.placeholder}
                  register={item.register}
                  error={!!get(errors, item.name)}
                  helperText={get(errors, item.name)?.message}
                  fullWidth
                  inputMaxLength={item.inputMaxLength}
                />
              );
            },
          )}

          {phoneNumberRequired && (
            <EAPhoneInput
              control={control}
              name="phone"
              errors={errors}
              label={t("numer_telefonu")}
              required
              fullWidth
            />
          )}
        </div>

        {showInvoiceOptions && (
          <>
            <h4 className="swimming-course-offer__form-title">{t("faktura")}</h4>
            <InvoiceButtons invoiceType={invoiceType} setInvoiceType={setInvoiceType} />
          </>
        )}

        {showInvoiceOptions && invoiceType !== "noInvoice" && (
          <div className="swimming-course-offer__form-invoice">
            {invoiceType === "recipientAndPurchaserInvoice" ? (
              <RecipientAndPurchaserForm
                register={register}
                errors={errors}
                control={control}
                setValue={setValue}
                countryCodes={countryCodes}
                countryCodesError={countryCodesError}
              />
            ) : (
              <>
                <h4 className="swimming-course-offer__form-title">{t("dane_do_faktury")}</h4>
                <div className="swimming-course-offer__form-fields">
                  {invoiceForm(register, invoiceType === "companyInvoice").map(
                    (item: FormElementModel, index: number) => (
                      <React.Fragment key={index}>
                        {item.name === "street" && invoiceType === "companyInvoice" && (
                          <EANipInput
                            control={control}
                            name="NIP"
                            countryCodeFieldName="nipCountryCode"
                            setValue={setValue}
                            errors={errors}
                            label={t("nip")}
                            countries={countryCodes}
                            showCountrySelect={!countryCodesError}
                            fullWidth
                          />
                        )}
                        {item.name && (
                          <EATextField
                            label={item.text}
                            name={item.name}
                            type={item.type as "text" | "email" | "password" | "number"}
                            placeholder={item.placeholder}
                            register={item.register}
                            error={!!get(errors, item.name)}
                            helperText={get(errors, item.name)?.message}
                            fullWidth
                            inputMaxLength={item.inputMaxLength}
                          />
                        )}
                      </React.Fragment>
                    ),
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <ConsentInputs
          errors={errors}
          control={control}
          setValue={setValue}
          email={email}
          title={t("zgody")}
          newsletter
        />

        {error && <EAAlert type="error" message={error} />}

        <div className="swimming-course-offer__price-row">
          <span className="swimming-course-offer__price-label">Do zapłaty</span>
          <span className="swimming-course-offer__price-value">{priceLabel}</span>
        </div>

        <div className="swimming-course-offer__form-actions">
          {!loading && paymentUrl ? (
            <>
              <EAButton
                title={t("przejdz_do_platnosci")}
                fullWidth
                href={paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => window.open(paymentUrl, "_blank", "noreferrer")}
              />
              <span className="body-text-small text-color-dark-gray">
                {t("za_chwile_zostaniesz_automatycznie_przeniesiony_do_systemu_platnosci")}
              </span>
            </>
          ) : loading && hasSubmitted ? (
            <LoadingSpinner />
          ) : (
            <>
              <EAButton
                variant="outlined"
                title={t("anuluj")}
                onClick={onCancel}
                disabled={loading}
                inputType="button"
                fullWidth
              />
              <EAButton
                title={t("zaplac")}
                loading={loading}
                inputType="submit"
                fullWidth
              />
            </>
          )}
        </div>
      </div>
    </form>
  );
};

export default SwimmingCourseOfferForm;
